import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Reservation, ReservationListFilters, Shift, SortType } from '../model/reservation';
import { Common } from './common-methods';
import { Advertisment } from '../model/login-response.dto';
import { AppSettings } from '@model/app-settings';
import { FontSize } from '../settings-form/settings-form.component';

export type CapacitorStorageKey = 'CachedReservationLists'
  | 'ExplicitLogout'
  | 'ListFiltersAndSorting'
  | 'Ads'
  | 'AdsTimesDisplayed'
  | 'AdsNextDateTime'
  | 'TablesPlanZoom'
  | 'TablesPlanOptions'
  | 'LatestActiveView'
  | 'HasOpenedTablesPlan'
  | 'AppSettings';

@Injectable({
  providedIn: 'root'
})
export class CapacitorStorageService {

  constructor() { }

  async getItem(key: CapacitorStorageKey): Promise<string | null> {
    const { value } = await Preferences.get({ key });

    return value;
  }

  async setItem(key: CapacitorStorageKey, value: string): Promise<void> {
    await Preferences.set({
      key,
      value
    });
  }

  async clearValue(key: CapacitorStorageKey): Promise<void> {
    await Preferences.remove({ key });
  }

  async clear(): Promise<void> {
    await Preferences.clear();
  }

  async clearButLeaveCredentials(): Promise<void> {
    this.clearValue('CachedReservationLists');
  }

  // for offline usage
  async getCachedReservationLists(): Promise<{ date: Date, reservations: Reservation[] }[]> {
    const value = await this.getItem('CachedReservationLists');
    return JSON.parse(value ?? '[]');
  }

  // for offline usage
  setCachedReservationLists(value: { date: Date, reservations: Reservation[] }[]) {
    this.setItem('CachedReservationLists', JSON.stringify(value));
  }

  // true if user explicitly logged out
  async getExplicitLogout(): Promise<boolean | null> {
    const value = await this.getItem('ExplicitLogout');
    if (Common.isNullOrEmpty(value)) {
      return null;
    }
    return JSON.parse(value ?? '{}') === true;
  }

  setExplicitLogout(value: boolean) {
    this.setItem('ExplicitLogout', JSON.stringify(value));
  }

  async getListFiltersAndSorting(): Promise<{ filters: ReservationListFilters, sortType: SortType, sortDesc: boolean, selectedShift: Shift | null | undefined }> {

    const defaultValue = {
      filters: {
        groupByStatus: false,
        withDietary: false,
        withType: false,
        withResvTags: false,
        withNotes: false,
        withDiscount: false,
        withBill: false,
        withCake: false,
        withSource: false,
        withFood: false,
        withDrink: false,
        withTokenization: false,
        //withDeposit: false
        reservationCategories: [],
      },
      sortType: SortType.Time,
      sortDesc: false,
      selectedShift: null
    };

    const value = await this.getItem('ListFiltersAndSorting');
    if (Common.isNullOrEmpty(value)) {
      return defaultValue;
    }
    return { ...defaultValue, ...JSON.parse(value as string) };
  }

  setListFiltersAndSorting(value: { filters: ReservationListFilters, sortType: SortType, sortDesc: boolean, selectedShift: Shift | null | undefined }) {
    this.setItem('ListFiltersAndSorting', JSON.stringify(value));
  }

  setAds(ads: Advertisment | null) {
    try {
      if (ads == undefined) {
        ads = null;
      }

      this.setItem("Ads", JSON.stringify(ads));
    }
    catch {
      this.clearValue("Ads");
    }
  }

  async getAds(): Promise<Advertisment | null> {
    try {
      const value = await this.getItem('Ads');

      if (Common.isNullOrEmpty(value)) {
        return null;
      }

      return JSON.parse(value || "{}");
    }
    catch {
      return null;
    }
  }

  setAdsTimesDisplayed(times: number | null) {
    try {
      if (times == null) {
        times = 0;
      }

      this.setItem("AdsTimesDisplayed", JSON.stringify(times));
    }
    catch {
      this.clearValue("AdsTimesDisplayed");
    }
  }

  async getAdsTimesDisplayed(): Promise<number> {
    try {
      const value = await this.getItem('AdsTimesDisplayed');

      if (Common.isNullOrEmpty(value)) {
        return 0;
      }

      return JSON.parse(value || "0");
    }
    catch {
      return 0;
    }
  }

  setAdsNextDisplayDayTime(times: number | null) {
    try {
      if (times == null) {
        times = 0;
      }

      this.setItem("AdsNextDateTime", JSON.stringify(times));
    }
    catch {
      this.clearValue("AdsNextDateTime");
    }
  }

  async getAdsNextDisplayDayTime(): Promise<number> {
    try {
      const value = await this.getItem('AdsNextDateTime');

      if (Common.isNullOrEmpty(value)) {
        return 0;
      }

      return JSON.parse(value || "0");
    }
    catch {
      return 0;
    }
  }

  async getTablesPlanZoom(): Promise<number | null> {
    const value = await this.getItem('TablesPlanZoom');
    if (value == null || isNaN(parseFloat(value))) {
      return null;
    }
    return parseFloat(value);
  }

  setTablesPlanZoom(value: number) {
    this.setItem('TablesPlanZoom', `${value}`);
  }

  async getTablesPlanOptions(): Promise<{
    showSurname: boolean,
    showProgress: boolean,
    showTablesConnection: boolean,
  }> {
    const value = await this.getItem('TablesPlanOptions');

    if (Common.isNullOrEmpty(value)) {
      return {
        showSurname: false,
        showProgress: true,
        showTablesConnection: true
      };
    }

    return JSON.parse(value as string);
  }

  setTablesPlanOptions(value: {
    showSurname: boolean,
    showProgress: boolean,
    showTablesConnection: boolean,
  }) {
    this.setItem('TablesPlanOptions', JSON.stringify(value));
  }

  async getLatestActiveView(): Promise<'ReservationsList' | 'TablesPlan'> {
    const value = await this.getItem('LatestActiveView');

    if (Common.isNullOrEmpty(value)) {
      return 'ReservationsList';
    }

    return value as 'ReservationsList' | 'TablesPlan';
  }

  setLatestActiveView(value: 'ReservationsList' | 'TablesPlan') {
    this.setItem('LatestActiveView', value);
  }

  async getHasOpenedTablesPlan(): Promise<boolean> {
    const value = await this.getItem('HasOpenedTablesPlan');

    if (Common.isNullOrEmpty(value) || value?.toLowerCase() !== 'true') {
      return false;
    }

    return true;
  }

  setHasOpenedTablesPlan(value: boolean) {
    this.setItem('HasOpenedTablesPlan', `${value}`);
  }

  async getAppSettings(): Promise<AppSettings> {
    const value = await this.getItem('AppSettings');

    if (Common.isNullOrEmpty(value)) {
      return {
        fontSize: {
          reservationList: FontSize.Default,
          reservationForm: FontSize.Default,
          tablesPlan: FontSize.Default,
        }
      };
    }

    return JSON.parse(value as string);
  }

  setAppSettings(value: AppSettings) {
    this.setItem('AppSettings', JSON.stringify(value));
  }

}
